import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type RecordHistoryResult } from '@whinerp/client';
import { ErrorState, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { AuditEntries } from '../shared/audit-entries.js';

/**
 * One record's History (F-018).
 *
 * The same list Setup's audit log renders, from the same component, so the two
 * cannot come to disagree about how a change reads. Behind the same authority
 * as reading the record: somebody who may open a contact may see who changed
 * it, which is not a setup question.
 */
export function RecordHistory({
  objectKey,
  recordId,
}: {
  objectKey: string;
  recordId: string;
}): JSX.Element {
  const t = useT();
  const history = useQuery<RecordHistoryResult, ApiError>({
    queryKey: ['record-history', objectKey, recordId],
    queryFn: () => api.recordHistory({ path: { objectKey, recordId }, query: { limit: 25 } }),
  });

  return (
    <Panel data-region="history">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('audit.history')}</SectionLabel>
        {history.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : history.isError ? (
          <ErrorState
            title={t('state.error')}
            body={history.error.message}
            onRetry={() => void history.refetch()}
          />
        ) : (
          <AuditEntries entries={history.data.entries} emptyLabel={t('audit.history.none')} />
        )}
      </div>
    </Panel>
  );
}
